import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type CommunicationAudience,
  type CommunicationInput,
  communicationsApi,
} from '@/services/api/communications/communications';

export const communicationsKeys = {
  all: ['communications'] as const,
  detail: (id: string) => ['communications', 'detail', id] as const,
  recipients: (id: string) => ['communications', 'recipients', id] as const,
  audience: (audience: CommunicationAudience, gradeLevels: number[], includeArrivingFamilies: boolean) =>
    ['communications', 'audience', audience, gradeLevels, includeArrivingFamilies] as const,
};

export function useCommunicationsQuery() {
  return useQuery({ queryKey: communicationsKeys.all, queryFn: () => communicationsApi.list() });
}

export function useCommunicationQuery(id: string, enabled = true) {
  return useQuery({ queryKey: communicationsKeys.detail(id), queryFn: () => communicationsApi.get(id), enabled });
}

export function useCommunicationRecipientsQuery(id: string, enabled = true) {
  return useQuery({
    queryKey: communicationsKeys.recipients(id),
    queryFn: () => communicationsApi.recipients(id),
    enabled,
  });
}

/** Drives the composer's live count, so the number shown is the number that will be mailed. */
export function useAudiencePreviewQuery(
  audience: CommunicationAudience,
  gradeLevels: number[],
  includeArrivingFamilies: boolean
) {
  return useQuery({
    queryKey: communicationsKeys.audience(audience, gradeLevels, includeArrivingFamilies),
    queryFn: () => communicationsApi.previewAudience(audience, gradeLevels, includeArrivingFamilies),
    enabled: audience === 'all-parents' || gradeLevels.length > 0,
  });
}

export function useCreateCommunicationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CommunicationInput) => communicationsApi.create(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: communicationsKeys.all }),
  });
}

export function useUpdateCommunicationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<CommunicationInput> }) =>
      communicationsApi.update(id, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: communicationsKeys.all });
      queryClient.invalidateQueries({ queryKey: communicationsKeys.detail(variables.id) });
    },
  });
}

export function useRemoveCommunicationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => communicationsApi.remove(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: communicationsKeys.all });
      queryClient.invalidateQueries({ queryKey: communicationsKeys.detail(id) });
    },
  });
}

export function useSendCommunicationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => communicationsApi.send(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: communicationsKeys.all });
      queryClient.invalidateQueries({ queryKey: communicationsKeys.detail(id) });
    },
  });
}

export function useScheduleCommunicationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, scheduledFor }: { id: string; scheduledFor: string }) =>
      communicationsApi.schedule(id, scheduledFor),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: communicationsKeys.all });
      queryClient.invalidateQueries({ queryKey: communicationsKeys.detail(variables.id) });
    },
  });
}

export function useCancelCommunicationScheduleMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => communicationsApi.cancelSchedule(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: communicationsKeys.all });
      queryClient.invalidateQueries({ queryKey: communicationsKeys.detail(id) });
    },
  });
}
